import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import { DashboardTitle } from "../../components/DashboardTitle";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { BsCopy, BsMessenger } from "react-icons/bs";
import { BiMessageDetail } from "react-icons/bi";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/store";
// import { currencyFormat, showCurrency } from "../../utility/currencyView";

// Response data type definitions
interface ProductImage {
  uuid: string;
  media_url: string;
  media_title: string;
}

interface Product {
  uuid: string;
  product_title: string;
  product_description: string;
  yourself_price: number;
  yourself_price_ngn:number;
  yourself_price_zar:number;
  friends_price: number;
  friends_price_ngn: number;
  friends_price_zar: number;
  currency: string;
  location: string;
  category: string;
  minimum_quantity: number;
}

interface Order {
  uuid: string;
  product_uuid: string;
  quantity: number;
  order_deadline: string;
}

interface ApiResponse {
  data: {
    images: ProductImage[];
    order_deadline: string;
    product: Product;
    order: Order;
    first_name: string;
    last_name: string;
    profile_picture: string;
  };
  message: string;
  success: boolean;
}

export const InviteFriends = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<ApiResponse | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const currencyView = "$"

  useEffect(() => {
    const fetchInviteFriendsData = async () => {
      try {
        // Get the reference from URL search params
        const searchParams = new URLSearchParams(location.search);
        const reference = searchParams.get("reference") || searchParams.get("trxref");

        if (!reference) {
          setError("Missing reference parameter");
          setIsLoading(false);
          return;
        }

        // Make API call with the reference
        const response = await api.get(
          `${environment.baseUrl}/product/invite-friends?reference=${reference}`
        );

        setResponseData(response.data);
        setIsLoading(false);

        // Show share modal after 3 seconds
        setTimeout(() => {
          setShowShareModal(true);
        }, 3000);
      } catch (error) {
        console.error("Error fetching invite friends data:", error);
        setError("Failed to fetch data");
        setIsLoading(false);
      }
    };

    fetchInviteFriendsData();
  }, [location.search]);

  // Countdown timer effect
  useEffect(() => {
    if (!responseData?.data?.order_deadline) return;

    const calculateTimeLeft = () => {
      const difference = new Date(responseData.data.order_deadline).getTime() - new Date().getTime();
      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [responseData]);

  // Image carousel controls
  const nextImage = () => {
    if (responseData?.data?.images?.length) {
      setActiveImageIndex((prev) => (prev + 1) % responseData.data.images.length);
    }
  };

  const prevImage = () => {
    if (responseData?.data?.images?.length) {
      setActiveImageIndex((prev) =>
        prev === 0 ? responseData.data.images.length - 1 : prev - 1
      );
    }
  };

  // Sharing handlers with updated URL parameters
  const getShareUrl = () => {
    if (!responseData?.data?.order?.uuid || !responseData?.data?.product?.uuid || !responseData?.data?.order?.quantity) {
      return "luudu.com/invited-friends-checkout";
    }
    
    const productUuid = responseData.data.order.product_uuid || responseData.data.product.uuid;
    const orderUuid = responseData.data.order.uuid;
    const quantity = responseData.data.order.quantity;
    
    const firstName = responseData.data.first_name || '';
    const lastName = responseData.data.last_name || '';
    
    return `luudu.com/invited-friends-checkout?p=${productUuid}&o=${orderUuid}&q=${quantity}&f=${encodeURIComponent(firstName)}&l=${encodeURIComponent(lastName)}`;
  };

  const shareMessage = "Hi there join me to purchase this product on Luudu";
  const shareUrl = getShareUrl();
  const fullShareMessage = `${shareMessage} ${shareUrl}`;

  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(fullShareMessage)}`, "_blank");
  };

  const shareViaFacebook = () => {
    window.open(
      `https://www.facebook.com/dialog/send?link=${encodeURIComponent(
        shareUrl
      )}&app_id=YOUR_FACEBOOK_APP_ID&redirect_uri=${encodeURIComponent(
        window.location.href
      )}`,
      "_blank"
    );
  };

  const shareViaSMS = () => {
    window.open(`sms:?body=${encodeURIComponent(fullShareMessage)}`, "_blank");
  };

  // Countdown Timer Component
  const CountdownTimer = () => (
    timeLeft && (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
        <h3 className="text-lg md:text-xl font-semibold text-center text-blue-800 mb-3">
          Time Left to Purchase
        </h3>
        <div className="flex justify-center gap-4">
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.days}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Days</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.hours}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Hours</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.minutes}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Min</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.seconds}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Sec</p>
          </div>
        </div>
        <p className="text-center text-sm text-blue-600 mt-4">
          Group purchase closing soon! Invite friends now for better prices.
        </p>
      </div>
    )
  );

  return (
    <section className="web_padding_x web_padding_y !pt-5">
      <DashboardTitle text="Invite Friends"></DashboardTitle>

      <div className="mt-8">
        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : responseData?.data ? (
          <div>
            {/* Mobile view - Countdown Timer at the top */}
            <div className="md:hidden mb-6">
              <CountdownTimer />
            </div>

            {/* Desktop 2-column layout */}
            <div className="flex flex-col md:flex-row md:gap-6">
              {/* Left Column - Product Details */}
              <div className="w-full md:w-2/3 bg-white rounded-xl shadow-md overflow-hidden">
                {/* Product Carousel */}
                <div className="relative h-64 sm:h-80 bg-gray-100">
                  {responseData.data.images && responseData.data.images.length > 0 ? (
                    <>
                      <img
                        src={responseData.data.images[activeImageIndex].media_url}
                        alt={responseData.data.images[activeImageIndex].media_title || "Product image"}
                        className="w-full h-full object-cover"
                      />
                      {/* Carousel controls with React Icons */}
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md"
                        aria-label="Previous image"
                      >
                        <FiChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md"
                        aria-label="Next image"
                      >
                        <FiChevronRight className="h-6 w-6" />
                      </button>
                      {/* Dot indicators */}
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                        {responseData.data.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`h-2 w-2 rounded-full ${
                              index === activeImageIndex ? "bg-blue-600" : "bg-gray-300"
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No images available</p>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">
                        {responseData.data.product.product_title}
                      </h2>
                      <p className="text-gray-600 mt-1">{responseData.data.product.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="font-medium">{responseData.data.product.category}</p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6">{responseData.data.product.product_description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Yourself Price</p>
                      <p className="text-xl font-bold">
                        {currencyView}{" "}
                        {responseData.data.product.yourself_price}
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Friends Price</p>
                      <p className="text-xl font-bold text-green-600">
                        {currencyView}{" "}
                        {responseData.data.product.friends_price}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Minimum Quantity</p>
                      <p className="text-xl font-bold">
                        {responseData.data.product.minimum_quantity}
                      </p>
                    </div>
                  </div>

                  <button 
                    onClick={() => setShowShareModal(true)} 
                    className="w-full bg-[#0e203f} text-white py-3 px-4 rounded-lg mt-2 font-medium hover:bg-blue-700 transition"
                  >
                    Invite friends to buy
                  </button>
                </div>
              </div>

              {/* Right Column - Time Remaining (desktop only) */}
              <div className="hidden md:block md:w-1/3">
                <CountdownTimer />
                
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-5 mt-6">
                  <h3 className="font-semibold text-yellow-800 mb-2">Why invite friends?</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-yellow-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-yellow-700">Save money with group discounts</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-yellow-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-yellow-700">Easily reach minimum order quantity</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-yellow-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-yellow-700">Share shipping costs with your group</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
            No product data available
          </div>
        )}
      </div>

      {/* Share Modal with React Icons */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Share with Friends</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <p className="text-gray-600 text-center mb-6">
              Invite friends to join this group purchase and get a better price!
            </p>
            
            <div className="flex justify-center gap-8 mb-6">
              {/* WhatsApp with React Icon */}
              <button
                onClick={shareViaWhatsApp}
                className="flex flex-col items-center text-gray-700 hover:text-green-600"
              >
                <div className="bg-green-100 p-4 rounded-full mb-2">
                  <FaWhatsapp className="h-8 w-8 text-green-600" />
                </div>
                <span className="text-sm">WhatsApp</span>
              </button>
              
              {/* Facebook Messenger with React Icon */}
              <button
                onClick={shareViaFacebook}
                className="flex flex-col items-center text-gray-700 hover:text-blue-600"
              >
                <div className="bg-blue-100 p-4 rounded-full mb-2">
                  <BsMessenger className="h-8 w-8 text-blue-600" />
                </div>
                <span className="text-sm">Messenger</span>
              </button>
              
              {/* SMS with React Icon */}
              <button
                onClick={shareViaSMS}
                className="flex flex-col items-center text-gray-700 hover:text-yellow-600"
              >
                <div className="bg-yellow-100 p-4 rounded-full mb-2">
                  <BiMessageDetail className="h-8 w-8 text-yellow-600" />
                </div>
                <span className="text-sm">SMS</span>
              </button>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center mb-2">
              <p className="text-sm text-gray-500">Share this message:</p>
              <button
                onClick={() => {
                navigator.clipboard.writeText(shareUrl); // Changed to shareUrl
                // Optional: show a small toast/notification
                alert("Copied to clipboard!");
                }}
                className="text-gray-500 hover:text-blue-600 transition-colors flex items-center text-sm gap-2"
                title="Copy to clipboard"
              >
               Copy Link <BsCopy />
              </button>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-gray-700 text-sm">
              {fullShareMessage}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};